import { motion } from "framer-motion";
import { Calendar, Users, Clock, MapPin, ExternalLink } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};

const classesConducted = [
  {
    title: "Python Fundamentals",
    date: "August 2025",
    duration: "5 days",
    location: "Online",
    students: 34,
    ageGroup: "Grades 3-8",
    description: "Introduction to programming concepts through interactive exercises and fun projects.",
    topics: ["Variables & Data Types", "Conditional Logic", "Basic Functions", "Simple Games"],
    image: "/class1.webp"
  },
  {
    title: "Python Fundamentals",
    date: "January 2026", 
    duration: "5 days",
    location: "Online",
    students: 31,
    ageGroup: "Grades 3-12",
    description: "Introduction to programming concepts through interactive exercises and fun projects.",
    topics: ["Variables & Data Types", "Conditional Logic", "Basic Functions", "Simple Games"],
    image: "/class2.webp"
  },
  {
    title: "Data Science",
    date: "June 2026",
    duration: "3 hours",
    location: "South County Regional Library",
    students: 32,
    ageGroup: "Grades 3-12",
    description: "Learn how to analyze data and create visualizations using Python.",
    topics: ["Data Analysis", "Data Visualization", "Pandas", "Matplotlib", "Data Cleaning", "Data Processing"],
    image: "/class3.webp"
  },
  {
    title: "Python Fundamentals",
    date: "July 2026",
    duration: "5 days",
    location: "Online",
    students: 15,
    ageGroup: "Grades 3-12",
    description: "Introduction to programming concepts through interactive exercises and fun projects.",
    topics: ["Variables & Data Types", "Conditional Logic", "Basic Functions", "Simple Games"],
    image: "/api/placeholder/400/250"
  },
  {
    title: "Python Fundamentals",
    date: "August 2026",
    duration: "5 days",
    location: "Online",
    students: 38,
    ageGroup: "Grades 3-8",
    description: "Introduction to programming concepts through interactive exercises and fun lessons.",
    topics: ["Variables & Data Types", "Conditional Logic", "Basic Functions", "Classes & Objects"],
    image: "/class4.webp"
  },
  {
    title: "AI Fundamentals",
    date: "August 2026",
    duration: "4 hours",
    location: "Online",
    students: 11,
    ageGroup: "Grades 3-12",
    description: "An introduction to artificial intelligence concepts and how AI models learn.",
    topics: ["The Big 5 Ideas of AI", "Machine Learning & Datasets", "Algorithms", "Training an AI Model", "Bias in AI"],
    image: "/api/placeholder/400/250"
  },
];

export const ClassesCarouselSection = ({ studentNumber, classesNumber, schoolPartners }: { studentNumber: number; classesNumber: number; schoolPartners: number }) => {
  return (
    <section className="relative pt-24 pb-20 overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-6xl mx-auto space-y-16"
        >
          {/* Header */}
          <motion.div variants={item} className="text-center space-y-6">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground">
              Classes We've <span className="text-primary">Conducted</span>
            </h2>
            <motion.div
              variants={item}
              className="h-0.5 bg-gradient-to-r from-primary/30 to-transparent w-24 mx-auto"
            />
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              A showcase of our successful programming workshops and camps that have inspired young minds across the community.
            </p>
          </motion.div>

          {/* Carousel */}
          <motion.div variants={item} className="relative px-10 sm:px-12">
            <Carousel
              opts={{
                align: "start",
                loop: true,
                slidesToScroll: "auto",
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {classesConducted.map((classItem, index) => (
                  <CarouselItem key={index} className="pl-4 basis-full md:basis-1/2 lg:basis-1/3">
                    <Card className="group bg-white border-0 shadow-card hover:shadow-hero transition-shadow duration-300 overflow-hidden h-full flex flex-col">
                      <div className="relative h-48 overflow-hidden">
                        <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          {classItem.image !== "/api/placeholder/400/250" ? <img src={classItem.image} alt={classItem.title} width={400} height={250} className="w-full h-full object-cover" /> : <Calendar className="w-16 h-16 text-primary/40" />}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                        <Badge className="absolute top-4 right-4 bg-white/90 text-foreground">
                          {classItem.ageGroup}
                        </Badge>
                      </div>

                      <CardContent className="p-6 space-y-4 flex-1 flex flex-col">
                        <div className="space-y-2">
                          <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                            {classItem.title}
                          </h3>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {classItem.description}
                          </p>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            <span>{classItem.date}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="w-4 h-4" />
                            <span>{classItem.duration}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="w-4 h-4" />
                            <span>{classItem.location}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="w-4 h-4" />
                            <span>{classItem.students} students</span>
                          </div>
                        </div>

                        <div className="space-y-2 mt-auto">
                          <p className="text-sm font-medium text-foreground">Topics Covered:</p>
                          <div className="flex flex-wrap gap-1">
                            {classItem.topics.map((topic, topicIndex) => (
                              <Badge key={topicIndex} variant="secondary" className="text-xs">
                                {topic}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        {/* <Button
                          variant="outline"
                          className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-all"
                        >
                          View Details
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </Button> */}
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="flex" />
              <CarouselNext className="flex" />
            </Carousel>
          </motion.div>

          {/* Stats Section */}
          <motion.div 
            variants={item}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-12"
          >
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold text-primary">{classesNumber}</div>
              <div className="text-sm text-muted-foreground">Classes Conducted</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold text-primary">{studentNumber}+</div>
              <div className="text-sm text-muted-foreground">Students Taught</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold text-primary">{schoolPartners}</div>
              <div className="text-sm text-muted-foreground">School Partners</div>
            </div>
            <div className="text-center space-y-2">
              <div className="text-3xl font-bold text-primary">100%</div>
              <div className="text-sm text-muted-foreground">Would Return</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
